import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadMedia } from "../../media/utils/mediaUploadUtils";
import { useAuth } from "../../../context/AuthContext";
import { debug } from "../../../utils/debugLogger";

interface LogoUploadProps {
  logoUrl: string;
  logoMediaId?: string;
  onLogoChange: (url: string, mediaId: string) => void;
  error?: string;
  customerId: string;
  /** Slot heading. Defaults to the single-logo wording for callers that predate SGL-344. */
  label?: string;
  /** Line under the upload button. Defaults to the accepted-formats hint. */
  helpText?: string;
  /**
   * DOM id for the file input.
   *
   * SGL-344 mounts this component twice on the Brand form (light and dark
   * variants). It used to hardcode id="logo-upload", so two mounts produced
   * duplicate ids and clicking the second slot's label opened the *first*
   * slot's file picker — the label/htmlFor pair binds to whichever input the
   * document hits first. Every mount on a page needs its own id.
   */
  inputId?: string;
  /**
   * What the preview tile stands in for — the background this mark is meant to
   * sit on.
   *
   * The tile was hardcoded `bg-white`, which is fine for the dark-ink mark and
   * useless for the white-ink one: it rendered as an apparently empty box, so
   * the dark slot looked like the upload had silently failed. Previewing each
   * mark on the kind of background it exists for is also the only way to see
   * whether you uploaded the right file into the right slot.
   */
  previewOn?: "light" | "dark";
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  logoUrl,
  onLogoChange,
  error,
  customerId,
  label = "Brand Logo",
  helpText = "PNG or JPG up to 5MB",
  inputId = "logo-upload",
  previewOn = "light",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [cacheBustToken, setCacheBustToken] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userId, idToken } = useAuth(); // userId contains the email

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setUploadError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB");
      return;
    }

    if (!idToken || !userId) {
      setUploadError("Authentication required. Please log in again.");
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Upload using the existing media upload service
      const result = await uploadMedia({
        file,
        customerId,
        userEmail: userId, // userId is the email
        token: idToken,
        isLogo: true,
        mediaType: "processed",
        tags: ["logo", "brand-assets"],
        additionalMetadata: {
          usage: "company-logo",
          source: "brand-form",
        },
      });

      if (result.success && result.cloudFrontUrl) {
        debug.log("✅ Logo uploaded successfully:", {
          url: result.cloudFrontUrl,
          mediaId: result.mediaId,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Save canonical URL; cache-bust only in preview rendering.
        onLogoChange(result.cloudFrontUrl, result.mediaId || "");
        setCacheBustToken(String(Date.now()));
      } else {
        throw new Error(result.errorMessage || "Upload failed");
      }
    } catch (error) {
      debug.error("Error uploading logo:", error);
      setUploadError("Failed to upload logo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    onLogoChange("", "");
    setCacheBustToken(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const baseLogoUrl =
    logoUrl && logoUrl !== "https://placeholder.com/logo.png" ? logoUrl : "";
  const displayLogoUrl =
    baseLogoUrl && cacheBustToken
      ? `${baseLogoUrl}${baseLogoUrl.includes("?") ? "&" : "?"}v=${cacheBustToken}`
      : baseLogoUrl;

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2 }}
    >
      <motion.label
        className="block text-sm font-medium text-[#2f4941] mb-1.5"
        whileHover={{ scale: 1.01 }}
      >
        {label}
      </motion.label>

      <div className="space-y-3">
        {/* Logo Preview */}
        {!isUploading && displayLogoUrl && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative inline-block"
          >
            <img
              src={displayLogoUrl}
              alt={`Brand logo — for ${previewOn} backgrounds`}
              data-preview-on={previewOn}
              className={`h-24 w-auto max-w-[200px] object-contain rounded border-2 border-[#2f4941] p-2 ${
                previewOn === "dark" ? "bg-[#1b2a4a]" : "bg-white"
              }`}
            />
            <button
              type="button"
              onClick={handleRemoveLogo}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
            >
              <span className="material-icons text-sm">close</span>
            </button>
          </motion.div>
        )}

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={inputId}
            disabled={isUploading}
          />
          <label
            htmlFor={inputId}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              isUploading
                ? "border-gray-300 bg-gray-50 cursor-not-allowed"
                : "border-[#2f4941] text-[#2f4941] hover:bg-[#2f4941]/5"
            }`}
          >
            {isUploading ? (
              <>
                <motion.span
                  className="rounded-full h-4 w-4 border-b-2 border-[#2f4941] inline-block"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <span>Uploading logo...</span>
              </>
            ) : (
              <>
                <span className="material-icons text-[#2f4941]">
                  {displayLogoUrl
                    ? "edit"
                    : "cloud_upload"}
                </span>
                <span>
                  {displayLogoUrl
                    ? "Change Logo"
                    : "Upload Logo"}
                </span>
              </>
            )}
          </label>
          <p className="mt-1.5 text-xs text-gray-500">{helpText}</p>
        </div>

        {/* Errors */}
        <AnimatePresence>
          {(uploadError || error) && (
            <motion.p
              initial={{ height: 0, opacity: 0, y: -10 }}
              animate={{ height: "auto", opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="text-xs text-red-600"
            >
              {uploadError || error}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
